import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import PlaceholderManager from './PlaceholderManager';
import PreviewPane from './PreviewPane';
import CollapsibleSection from './CollapsibleSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import { promptsService } from '../services';

const { FiSave, FiArrowLeft, FiEye, FiCopy, FiFileText, FiCode, FiList, FiSettings, FiType, FiTag } = FiIcons;

const TemplateEditor = ({ isCreate = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getPrompt, addPrompt, updatePrompt } = usePrompts();
  const { success: showSuccess, error: showError } = useToast();
  const textareaRef = useRef(null);

  const isEditing = Boolean(id) && !isCreate;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [isLoading, setIsLoading] = useState(isEditing);

  const {
    state: copyState,
    error: copyError,
    handleAction: handleCopyAction,
    reset: resetCopyState,
    icon: copyIcon,
    text: copyText,
    classes: copyClasses,
  } = useButtonState({
    idleText: 'Copy',
    successText: 'Copied!',
    failedText: 'Failed',
    idleIcon: FiCopy,
    autoResetDelay: 2000,
  });

  const {
    state: saveState,
    error: saveError,
    handleAction: handleSaveAction,
    reset: resetSaveState,
    icon: saveIcon,
    text: saveText,
    classes: saveClasses,
  } = useButtonState({
    idleText: 'Save',
    loadingText: 'Saving...',
    successText: 'Saved!',
    failedText: 'Failed',
    idleIcon: FiSave,
    onSuccess: () => {
      showSuccess('Template saved successfully!');
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || 'Failed to save template';
      showError(errorMsg);
    },
    autoResetDelay: 2000,
  });

  useEffect(() => {
    if (isEditing) {
      const prompt = getPrompt(id);
      if (prompt) {
        setTitle(prompt.title);
        setContent(prompt.content);
        setTags(prompt.tags ? prompt.tags.join(', ') : '');
        setIsLoading(false);
      } else {
        // If prompt not found in local state, try to fetch it
        promptsService.getPrompt(id)
          .then(data => {
            setTitle(data.title);
            setContent(data.content);
            setTags(data.tags ? data.tags.join(', ') : '');
            setIsLoading(false);
          })
          .catch(err => {
            console.error('Failed to load prompt:', err);
            setIsLoading(false);
          });
      }
    } else {
      setTitle('');
      setContent('');
      setTags('');
      setIsLoading(false);
    }
  }, [id, isEditing, getPrompt, isCreate]);

  const handleSave = async (isAutoSave = false) => {
    const validationError = !title.trim() ? 'Title is required' : !content.trim() ? 'Content is required' : null;

    if (validationError) {
      if (!isAutoSave) {
        showError(validationError);
      }
      return;
    }

    const promptData = {
      title: title.trim(),
      content: content.trim(),
      tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
    };

    await handleSaveAction(async () => {
      if (isEditing) {
        await updatePrompt(id, promptData);
      } else {
        const newId = await addPrompt(promptData);
        navigate(`/editor/${newId}`);
      }
      setTimeout(() => {
        navigate('/');
      }, 1500);
    });
  };

  const handleCopy = async () => {
    if (!content) {
      showError('Nothing to copy');
      resetCopyState();
      return;
    }

    await handleCopyAction(async () => {
      await navigator.clipboard.writeText(content);
    });
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + placeholder + content.substring(end);
    
    setContent(newContent);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const extractPlaceholders = (text) => {
    const matches = text.match(/\{[^}]+\}/g) || [];
    return [...new Set(matches)];
  };

  const placeholders = extractPlaceholders(content);

  const mobileTabs = [
    { id: 'editor', label: 'Editor', icon: FiCode, color: 'neo-yellow', activeColor: 'text-black', bgColor: 'bg-neo-yellow' },
    { id: 'variables', label: 'Vars', icon: FiList, color: 'neo-purple', activeColor: 'text-black', bgColor: 'bg-neo-purple' },
    { id: 'preview', label: 'Preview', icon: FiEye, color: 'neo-green', activeColor: 'text-black', bgColor: 'bg-neo-green' },
    { id: 'metadata', label: 'Meta', icon: FiSettings, color: 'neo-blue', activeColor: 'text-black', bgColor: 'bg-neo-blue' },
  ];

  const MobileTabs = () => (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t-3 border-black flex z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {mobileTabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex flex-col items-center justify-center py-2 transition-all duration-150 relative border-r-2 border-black last:border-r-0 ${
              isActive 
                ? `${tab.bgColor} ${tab.activeColor}` 
                : 'bg-white text-gray-600 active:bg-gray-100'
            }`}
          >
            {isActive && (
              <motion.div
                layoutId="activeTabIndicator"
                className="absolute top-0 left-0 right-0 h-1 bg-black"
                transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
              />
            )}
            <SafeIcon icon={tab.icon} size={22} className={isActive ? 'drop-shadow-sm' : ''} />
            <span className={`text-[10px] font-black mt-1 uppercase tracking-wider ${isActive ? '' : 'font-bold'}`}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto pb-6 min-h-[calc(100vh-152px)] sm:min-h-[calc(100vh-100px)] md:min-h-[calc(100vh-80px)] flex flex-col pb-bottom-nav lg:pb-6 lg:px-6">
      {isLoading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="font-bold uppercase tracking-widest">Loading...</p>
          </div>
        </div>
      ) : (
      <>
      <div className="flex flex-col md:flex-row items-center justify-between mb-3 md:mb-6 gap-3 md:gap-4 flex-shrink-0 px-3 md:px-0">
        <div className="flex items-center space-x-3 w-full md:w-auto">
          <button
            onClick={() => navigate('/')}
            className="neo-btn-icon bg-white min-w-[44px] min-h-[44px] md:min-w-[52px] md:min-h-[52px] touch-target-md"
            title="Back to Library"
          >
            <SafeIcon icon={FiArrowLeft} size={20} />
          </button>
          <div className="flex-1 md:flex-none">
            <h1 className="text-base md:text-xl lg:text-2xl font-black text-black uppercase flex items-center gap-2">
              <SafeIcon icon={FiFileText} size={20} className="md:w-6 md:h-6" />
              <span className="truncate">{isEditing ? 'Edit Template' : 'New Template'}</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-end relative">
          <button
            onClick={handleCopy}
            className={`neo-btn flex items-center text-sm px-3 md:px-4 py-2 md:py-2.5 min-h-[44px] md:min-h-[48px] ${copyClasses}`}
            disabled={copyState === 'loading'}
          >
            <SafeIcon icon={copyIcon || FiCopy} size={16} />
            <span className="hidden sm:inline ml-2">{copyText}</span>
          </button>
          {copyState === 'failed' && copyError && (
            <div className="absolute top-full right-0 md:right-40 mt-2 bg-red-100 border-2 border-red-600 p-2 text-xs text-red-800 font-bold max-w-[calc(100vw-2rem)] md:max-w-xs z-50">
              {copyError}
            </div>
          )}
          <button
            onClick={() => handleSave()}
            className={`neo-btn neo-btn-primary flex items-center text-sm px-3 md:px-4 py-2 md:py-2.5 min-h-[44px] md:min-h-[48px] ${saveClasses}`}
            disabled={saveState === 'loading'}
          >
            <SafeIcon icon={saveIcon || FiSave} size={16} />
            <span className="ml-2">{saveText}</span>
          </button>
          {saveState === 'failed' && saveError && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 md:right-0 md:left-auto md:translate-x-0 mt-2 bg-red-100 border-2 border-red-600 p-2 text-xs text-red-800 font-bold max-w-[calc(100vw-2rem)] md:max-w-xs z-50">
              {saveError}
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:grid flex-1 min-h-0 grid-cols-3 gap-6">
        <div className="col-span-2 flex flex-col gap-4 overflow-hidden h-full">
          <CollapsibleSection title="METADATA" headerColor="bg-neo-blue" defaultExpanded={false}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="title-desktop" className="block text-xs font-bold text-black mb-1 uppercase">
                    Title
                  </label>
                  <input
                    id="title-desktop"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter Title..."
                    className="neo-input text-sm input-touch"
                  />
                </div>
                <div>
                  <label htmlFor="tags-desktop" className="block text-xs font-bold text-black mb-1 uppercase">
                    Tags
                  </label>
                  <input
                    id="tags-desktop"
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="tag1, tag2..."
                    className="neo-input text-sm input-touch"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <div className="neo-card p-0 flex-1 flex flex-col min-h-0 bg-white">
            <div className="neo-window-header bg-neo-yellow flex justify-between items-center">
              <span className="text-xs font-bold uppercase">EDITOR</span>
            </div>
            <div className="flex-1 flex flex-col p-4 min-h-0">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Type prompt here... Use {variable} for dynamic fields"
                className="neo-input font-mono text-sm leading-relaxed flex-1 resize-none p-4 focus:ring-2 ring-black border-2 border-black"
                spellCheck="false"
              />
              <div className="mt-2 flex justify-between items-center text-xs text-gray-500 font-mono">
                 <span>{content.length} chars</span>
                 <span className="bg-black text-white px-2 py-0.5 font-bold">Use {'{'}variable{'}'} for dynamic fields</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-1 flex flex-col gap-4 h-full overflow-y-auto overscroll-contain">
          <CollapsibleSection title="VARIABLES" headerColor="bg-neo-purple" defaultExpanded={true}>
            <PlaceholderManager
              placeholders={placeholders}
              onInsertPlaceholder={insertPlaceholder}
            />
          </CollapsibleSection>

          <CollapsibleSection title="PREVIEW" headerColor="bg-neo-green" defaultExpanded={true}>
            <PreviewPane
              content={content}
              placeholders={placeholders}
            />
          </CollapsibleSection>
        </div>
      </div>

      <div className="lg:hidden flex-1 flex flex-col min-h-0 overflow-hidden" style={{ paddingBottom: 'calc(60px + env(safe-area-inset-bottom, 0px))' }}>
        <AnimatePresence mode="wait">
          {activeTab === 'editor' && (
            <motion.div
              key="editor"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="flex-1 flex flex-col min-h-0 overflow-hidden px-3 pt-2"
            >
              <div className="neo-card p-0 flex-1 flex flex-col min-h-0 bg-white overflow-hidden">
                <div className="neo-window-header bg-neo-yellow flex justify-between items-center flex-shrink-0">
                  <span className="text-sm font-black uppercase tracking-wide">Editor</span>
                  <span className="text-xs font-mono text-gray-600">{content.length} chars</span>
                </div>
                <div className="flex-1 flex flex-col p-3 min-h-0 overflow-hidden">
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Type your prompt here...&#10;&#10;Use {variable} syntax for dynamic fields.&#10;Example: Hello {name}, welcome to {place}!"
                    className="neo-input font-mono text-[15px] leading-relaxed flex-1 resize-none p-4 focus:ring-2 ring-black border-2 border-black min-h-0"
                    spellCheck="false"
                  />
                  <div className="mt-2 flex items-center justify-center">
                    <span className="bg-black text-white px-3 py-1.5 font-bold text-xs uppercase tracking-wide rounded-sm">
                      Tip: Use {'{'}variable{'}'} for placeholders
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'variables' && (
            <motion.div
              key="variables"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="flex-1 flex flex-col min-h-0 overflow-auto scroll-mobile px-3 pt-2 pb-4"
            >
              <PlaceholderManager
                placeholders={placeholders}
                onInsertPlaceholder={insertPlaceholder}
              />
            </motion.div>
          )}

          {activeTab === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="flex-1 flex flex-col min-h-0 overflow-auto scroll-mobile px-3 pt-2 pb-4"
            >
              <div className="neo-card bg-white flex-shrink-0">
                <div className="neo-window-header bg-neo-green flex-shrink-0">
                  <span className="text-sm font-black uppercase tracking-wide">Preview</span>
                </div>
                <div className="p-4">
                  <PreviewPane
                    content={content}
                    placeholders={placeholders}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'metadata' && (
            <motion.div
              key="metadata"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="flex-1 flex flex-col min-h-0 overflow-auto scroll-mobile px-3 pt-2 pb-4"
            >
              <div className="neo-card bg-white">
                <div className="neo-window-header bg-neo-blue flex-shrink-0">
                  <span className="text-sm font-black uppercase tracking-wide">Metadata</span>
                </div>
                <div className="p-5 space-y-6">
                  <div>
                    <label htmlFor="title-mobile" className="flex items-center gap-2 text-xs font-black text-black mb-3 uppercase tracking-wide">
                      <SafeIcon icon={FiType} size={14} />
                      Title
                    </label>
                    <input
                      id="title-mobile"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Give your template a name..."
                      className="neo-input text-base input-touch w-full font-medium"
                    />
                  </div>
                  <div>
                    <label htmlFor="tags-mobile" className="flex items-center gap-2 text-xs font-black text-black mb-3 uppercase tracking-wide">
                      <SafeIcon icon={FiTag} size={14} />
                      Tags
                    </label>
                    <input
                      id="tags-mobile"
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="productivity, writing, ai..."
                      className="neo-input text-base input-touch w-full"
                    />
                    <p className="mt-2 text-xs text-gray-500">Separate tags with commas</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </>
      )}

      <MobileTabs />
    </div>
  );
};

export default TemplateEditor;
